import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  PercentageBasedOn,
  RatePayee,
  VesselRate,
} from 'anchor-shared/lib/model/entity/vessel-rate';
import { VesselRateDAO } from '../../../../../../../../core/services/dao/vesselRateDAO';
import { NumberFormatterService } from '../../../../../../../../core/services/number.formatter.service';
import { AppTextMask } from '../../../../../../../../core/models/interface/app-text-mask';
import { FormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { User } from 'anchor-shared/lib/model/entity/user';
import { UserDAO } from '../../../../../../../../core/services/dao/userDAO';
import { VesselAvailabilityDAO } from '../../../../../../../../core/services/dao/VesselAvailabilityDAO';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { VesselSplitRate } from 'anchor-shared/lib/model/entity/vesselSplitRate';
import { VesselSplitRateDAO } from '../../../../../../../../core/services/dao/vesselSplitRateDAO';
import { ContainsSubscriptions } from '../../../../../../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../../../../../../core/pure-functions/rx-helper';

@Component({
  selector: 'app-add-edit-vessel-rate-dialog',
  templateUrl: './add-edit-vessel-rate-dialog.component.html',
  styleUrls: ['./add-edit-vessel-rate-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddEditVesselRateDialogComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  vesselRate: VesselRate;
  snapshotRate: VesselRate;
  vesselId: number;
  isEdit: boolean;
  rateTypesForDropDown: { key: RateType; value: RateType }[];
  lineItemPayees: string[];
  currencyMask: AppTextMask;
  percentageMask: AppTextMask;
  rateIsTaxable = false;
  autocompleteControl: FormControl = new FormControl();
  autocompleteSplitRateControl: FormControl = new FormControl();
  userOptions: any;

  filteredUsers: Observable<User[]>;
  defaultRateType: RateType;
  submitInProgress = false;
  halfDayTotal: number;
  fullDayTotal: number;
  halfDayRetailPrice: number;
  fullDayRetailPrice: number;
  hourlyTotal: number;
  lineItemIsSplit: boolean = false;
  lineItemSplits: VesselSplitRate[] = [];
  lineItemSplitInProgress: boolean = false;
  numberOfSplits: number = 2;
  subs: Subscription[] = [];
  listPricePriceSelected: boolean = true;
  hourlyRetailPrice: number;
  splitSaveInProgress: boolean = false;
  isAdmin: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AddEditVesselRateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private vesselRateDAO: VesselRateDAO,
    private vesselSplitRateDAO: VesselSplitRateDAO,
    private numberFormatterService: NumberFormatterService,
    private userDao: UserDAO,
    private vesselAvailabilityDAO: VesselAvailabilityDAO,
  ) {
    this.isAdmin = this.data.isAdmin;
    this.vesselRate = this.data.vesselRate;
    if (
      this.vesselRate.percentBasedOn &&
      this.vesselRate.percentBasedOn === PercentageBasedOn.LIST_PRICE
    ) {
      this.listPricePriceSelected = true;
    } else if (
      this.vesselRate.percentBasedOn &&
      this.vesselRate.percentBasedOn === PercentageBasedOn.RETAIL
    ) {
      this.listPricePriceSelected = false;
    }

    if (this.vesselRate.vesselSplitRates.length > 0) {
      this.lineItemIsSplit = true;
      this.numberOfSplits = this.vesselRate.vesselSplitRates.length;
      this.lineItemSplits = this.vesselRate.vesselSplitRates;
    } else if (this.data.newSplit) {
      this.lineItemIsSplit = true;
      this.numberOfSplits = 2;
      this.createSplits(this.numberOfSplits);
    }
    this.snapshotRate = this.vesselRate;
    this.isEdit = this.vesselRate.id > 0;

    if (!this.isEdit) {
      this.vesselRate.generatePayouts = true;
      this.vesselRate.percentageBasedRatePercent =
        this.vesselRate.percentageBasedRatePercent * 100;
    }

    this.vesselRate.markupPercentage = this.vesselRate.markupPercentage * 100;
    this.vesselRate.taxPercentage = this.vesselRate.taxPercentage * 100;
    this.vesselRate.percentageBasedRatePercent =
      this.vesselRate.percentageBasedRatePercent * 100;

    this.halfDayTotal = this.data.halfDayTotal;
    this.fullDayTotal = this.data.fullDayTotal;
    this.hourlyTotal = this.data.hourlyTotal;

    this.halfDayRetailPrice = this.data.halfDayRetailPrice;
    this.fullDayRetailPrice = this.data.fullDayRetailPrice;
    this.hourlyRetailPrice = this.data.hourlyRetailPrice;

    this.vesselId = this.data.vesselId;
    this.rateIsTaxable = this.vesselRate.taxPercentage > 0;
    this.defaultRateType = this.data.defaultRateType;
    if (this.vesselRate.rateType == null) {
      if (this.defaultRateType) {
        this.vesselRate.rateType = this.defaultRateType;
      } else {
        this.vesselRate.rateType = RateType.CustomTimeBased;
      }
    }
    this.currencyMask = this.numberFormatterService.createPriceMask();
    this.percentageMask = this.numberFormatterService.createPercentageMask();
  }

  onChangeMarkupPercentage($event) {
    this.vesselRate.markupPercentage =
      this.numberFormatterService.unMaskPercentage($event);
  }

  onChangeRatePercentage($event) {
    this.vesselRate.percentageBasedRatePercent =
      this.numberFormatterService.unMaskPercentage($event);

    const transformedPercentage =
      this.vesselRate.percentageBasedRatePercent / 100;

    let halfDayTotalToUse;
    let fullDayTotalToUse;
    let hourlyTotalToUse;
    if (this.listPricePriceSelected) {
      halfDayTotalToUse = this.halfDayTotal;
      fullDayTotalToUse = this.fullDayTotal;
      hourlyTotalToUse = this.hourlyTotal;
    } else {
      halfDayTotalToUse = this.halfDayRetailPrice;
      fullDayTotalToUse = this.fullDayRetailPrice;
      hourlyTotalToUse = this.hourlyTotal;
    }

    this.vesselRate.halfDayRate = parseFloat(
      (halfDayTotalToUse * transformedPercentage).toFixed(2),
    );
    this.vesselRate.fullDayRate = parseFloat(
      (fullDayTotalToUse * transformedPercentage).toFixed(2),
    );
    this.vesselRate.hourlyRate = parseFloat(
      (hourlyTotalToUse * transformedPercentage).toFixed(2),
    );
  }

  onChangeTaxPercentage($event) {
    this.vesselRate.taxPercentage =
      this.numberFormatterService.unMaskPercentage($event);
  }

  ngOnInit() {
    this.initDropdowns();

    const userSub = this.userDao.getUsersForPayouts().subscribe((data) => {
      this.userOptions = data;
      this.filteredUsers = this.autocompleteControl.valueChanges.pipe(
        startWith(''),
        map((val) => (val && val.length >= 4 ? this._filter(val) : [])),
      );
      if (this.vesselRate && this.vesselRate.payToUserId) {
        const user = this.userOptions.filter(
          (item) => this.vesselRate.payToUserId === item.id,
        )[0];
        this.autocompleteControl.setValue(user.firstName + ' ' + user.lastName);
      }
    });
    this.subs.push(userSub);
  }

  _filter(val: string): User[] {
    return this.userOptions.filter((s) =>
      new RegExp(val, 'gi').test(s.firstName + ' ' + s.lastName),
    );
  }

  initDropdowns(): void {
    this.rateTypesForDropDown = [
      {
        key: RateType.Captain,
        value: RateType.Captain,
      },
      {
        key: RateType.Crew,
        value: RateType.Crew,
      },
      {
        key: RateType.Owner,
        value: RateType.Owner,
      },
      {
        key: RateType.FuelIncluded,
        value: RateType.FuelIncluded,
      },
      {
        key: RateType.Cleaning,
        value: RateType.Cleaning,
      },
      {
        key: RateType.Gratuity,
        value: RateType.Gratuity,
      },
    ];

    this.lineItemPayees = ['Boat Owner', 'Captain', 'Crew'];

    if (this.isAdmin) {
      this.rateTypesForDropDown.push(
        {
          key: RateType.Custom,
          value: RateType.Custom,
        },
        {
          key: RateType.CustomTimeBased,
          value: RateType.CustomTimeBased,
        },
        {
          key: RateType.BookingFee,
          value: RateType.BookingFee,
        },
      );

      this.lineItemPayees.push('Anchor', 'Specific User');
    }
  }

  toggleTaxable(): void {
    this.rateIsTaxable = !this.rateIsTaxable;
    if (!this.rateIsTaxable) {
      this.vesselRate.taxPercentage = 0;
    }
  }

  save(splitInProgress?: boolean, saveRateAndSplits?: boolean) {
    if (this.vesselRate.percentageBasedRatePercent) {
      this.vesselRate.percentBasedOn = this.listPricePriceSelected
        ? PercentageBasedOn.LIST_PRICE
        : PercentageBasedOn.RETAIL;
    }
    if (splitInProgress) {
      this.createSplits();
      this.lineItemSplitInProgress = true;
    } else {
      this.submitInProgress = true;
      this.prepareRate();

      if (
        !this.isAdmin &&
        [
          RateType.Captain,
          RateType.Crew,
          RateType.Owner,
          RateType.FuelIncluded,
        ].includes(this.vesselRate.rateType)
      ) {
        this.vesselRate.markupPercentage = 0.176471;
      } else if (
        !this.isAdmin &&
        [
          RateType.Custom,
          RateType.CustomTimeBased,
          RateType.Gratuity,
          RateType.Cleaning,
        ].includes(this.vesselRate.rateType)
      ) {
        this.vesselRate.markupPercentage = 0.25;
      }

      if (this.isEdit) {
        if (
          this.vesselRate.rateType === 'Captain' ||
          this.vesselRate.rateType === 'Owner' ||
          this.vesselRate.rateType === 'Crew'
        ) {
          this.vesselRate.name = null;
        }

        this.vesselRateDAO
          .serverUpdate(this.vesselRate)
          .subscribe((updatedRate: VesselRate) => {
            if (saveRateAndSplits) {
              this.updateOrCreatSplitRates(updatedRate);
            } else {
              this.dialogRef.close(updatedRate);
            }
          });
      } else {
        this.vesselRate.vesselId = this.vesselId;
        this.vesselRateDAO
          .serverCreate(this.vesselRate)
          .subscribe((createdRate: VesselRate) => {
            this.vesselRate.id = createdRate.id;
            this.vesselRate.taxPercentage = this.vesselRate.taxPercentage * 100;
            this.vesselRate.markupPercentage =
              this.vesselRate.markupPercentage * 100;
            if (saveRateAndSplits) {
              this.updateOrCreatSplitRates(createdRate);
            } else {
              this.dialogRef.close(createdRate);
            }
          });
      }
    }
  }

  updateOrCreatSplitRates(rate: VesselRate) {
    let rateId = this.isEdit ? this.vesselRate.id : rate.id;
    let needsUpdating = [];
    let needsCreated = [];

    this.lineItemSplits.map((split) => {
      if (split.id < 0) {
        needsCreated.push({
          ...split,
          vesselRateId: rateId,
        });
      } else {
        needsUpdating.push(split);
      }
    });

    const updateSplitSub = this.vesselSplitRateDAO
      .updateMultiple(this.vesselId, needsUpdating)
      .subscribe((updatedSplits) => {
        const newSplitSub = this.vesselSplitRateDAO
          .createMultiple(this.vesselId, needsCreated)
          .subscribe((createdSplits) => {
            this.vesselAvailabilityDAO
              .updateFullAvailabilitiesForVessel(this.vesselId)
              .pipe(take(1))
              .subscribe(() => {
                this.submitInProgress = false;
                this.dialogRef.close(rate);
              });
          });
        this.subs.push(newSplitSub);
      });
    this.subs.push(updateSplitSub);
  }

  saveSplit() {
    this.splitSaveInProgress = true;
    this.save(null, true);
  }

  createSplits(splitCount: number = 2) {
    let evenSplitAmount = parseFloat((100 / splitCount).toFixed(2));
    console.log('evenSplitAmount', evenSplitAmount);
    if (this.lineItemSplits.length <= 0) {
      for (let i = 0; i < splitCount; i++) {
        this.lineItemSplits.push({
          id: (i + 1) * -1,
          vesselRateId: 1,
          name: '',
          ratePayee: i === 0 ? this.vesselRate.ratePayee : null,
          payToUserId:
            i === 0 && this.vesselRate.ratePayee === RatePayee.SPECIFIC_USER
              ? this.vesselRate.payToUserId
              : null,
          percentage: evenSplitAmount,
          generatePayouts: true,
        });
      }
    }
  }

  updateSplits() {
    let currentSplitCount = this.lineItemSplits.length;
    let splitsToAdd = this.numberOfSplits - currentSplitCount;
    let evenSplitAmount = parseFloat((100 / this.numberOfSplits).toFixed(2));
    if (splitsToAdd === 0) {
      return;
    } else if (splitsToAdd > 0) {
      this.lineItemSplits.map((split) => {
        split.percentage = evenSplitAmount;
      });
      for (let i = 0; i < splitsToAdd; i++) {
        this.lineItemSplits.push({
          id: (currentSplitCount + i + 1) * -1,
          vesselRateId: 1,
          name: '',
          ratePayee: null,
          payToUserId: null,
          percentage: evenSplitAmount,
          generatePayouts: true,
        });
      }
    } else {
      this.lineItemSplits = [];
      this.createSplits(this.numberOfSplits);
    }
  }

  rateTypeChange(): void {
    if (this.vesselRate.rateType !== RateType.Custom) {
      this.vesselRate.customRate = 0;
    } else {
      this.vesselRate.halfDayRate = 0;
      this.vesselRate.fullDayRate = 0;
      this.vesselRate.hourlyRate = 0;
    }

    this.vesselRate.percentageBasedRatePercent = 0;

    if (this.vesselRate.rateType === 'Booking Fee') {
      this.vesselRate.generatePayouts = false;
    } else {
      this.vesselRate.generatePayouts = true;
    }
  }

  cancel() {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  private prepareRate(): void {
    this.transformPercentages();
    this.unMaskPrices();
    if (this.vesselRate.rateType === RateType.FuelIncluded) {
      this.vesselRate.name = 'Fuel Included';
    }
    if (this.vesselRate.ratePayee !== RatePayee.SPECIFIC_USER) {
      this.vesselRate.payToUserId = null;
    }

    if (
      this.vesselRate.rateType === RateType.Gratuity ||
      this.vesselRate.rateType === RateType.BookingFee
    ) {
      this.vesselRate.name = this.vesselRate.rateType;
      if (this.vesselRate.rateType === RateType.BookingFee) {
        this.vesselRate.ratePayee = RatePayee.ANCHOR;
      }

      this.vesselRate.percentageBasedRatePercent =
        this.vesselRate.percentageBasedRatePercent / 100;
    }
  }

  private transformPercentages(): void {
    this.vesselRate.markupPercentage = this.vesselRate.markupPercentage / 100;
    this.vesselRate.taxPercentage = this.vesselRate.taxPercentage / 100;
  }

  private unMaskPrices(): void {
    this.vesselRate.customRate = this.numberFormatterService.unMaskPrice(
      this.vesselRate.customRate,
    );
    this.vesselRate.halfDayRate = this.numberFormatterService.unMaskPrice(
      this.vesselRate.halfDayRate,
    );
    this.vesselRate.fullDayRate = this.numberFormatterService.unMaskPrice(
      this.vesselRate.fullDayRate,
    );
    this.vesselRate.hourlyRate = this.numberFormatterService.unMaskPrice(
      this.vesselRate.hourlyRate,
    );
  }

  close(): void {
    this.dialogRef.close(null);
  }

  selectUser(event, user: User) {
    if (event.isUserInput) {
      this.vesselRate.payToUserId = user.id;
    }
  }

  selectUserForSplit(event, user: User, splitId) {
    if (event.isUserInput) {
      this.lineItemSplits.map((split) => {
        if (splitId === split.id) {
          split.payToUserId = user.id;
        }
      });
    }
  }

  disableSplitSave(): boolean {
    let isDisabled = false;
    let totalPercent = 0;
    if (this.splitSaveInProgress) {
      return true;
    }
    this.lineItemSplits.map((splitRate) => {
      if (typeof splitRate.percentage === 'string') {
        splitRate.percentage = parseFloat(splitRate.percentage);
        // @ts-ignore
        if (splitRate.percentage === 'NaN') {
          splitRate.percentage = 0;
        }
      }
      totalPercent += splitRate.percentage;
      if (!splitRate.name || !splitRate.ratePayee) {
        isDisabled = true;
      }
      if (
        (splitRate.name && splitRate.name.length < 1) ||
        (splitRate.ratePayee && splitRate.ratePayee.length < 1)
      ) {
        isDisabled = true;
      }
    });
    if (totalPercent > 100 || totalPercent < 99.99) {
      isDisabled = true;
    }
    return isDisabled;
  }

  disableSave(ctrl: FormControl): boolean {
    if (this.vesselRate.rateType === RateType.Captain) {
      return (
        !this.vesselRate.ratePayee ||
        !this.vesselRate.halfDayRate ||
        !this.vesselRate.fullDayRate ||
        !this.vesselRate.hourlyRate
      );
    } else if (this.vesselRate.rateType === RateType.Owner) {
      return (
        !this.vesselRate.ratePayee ||
        !this.vesselRate.halfDayRate ||
        !this.vesselRate.fullDayRate ||
        !this.vesselRate.hourlyRate
      );
    } else if (this.vesselRate.rateType === RateType.Crew) {
      return (
        !this.vesselRate.halfDayRate ||
        !this.vesselRate.fullDayRate ||
        !this.vesselRate.hourlyRate
      );
    } else if (this.vesselRate.rateType === RateType.Custom) {
      return (
        !this.vesselRate.name ||
        !this.vesselRate.customRate ||
        !this.vesselRate.ratePayee
      );
    } else if (this.vesselRate.rateType === RateType.CustomTimeBased) {
      return (
        !this.vesselRate.ratePayee ||
        !this.vesselRate.halfDayRate ||
        !this.vesselRate.fullDayRate ||
        !this.vesselRate.hourlyRate ||
        !this.vesselRate.name
      );
    } else if (this.vesselRate.rateType === RateType.FuelIncluded) {
      return (
        !this.vesselRate.ratePayee ||
        !this.vesselRate.halfDayRate ||
        !this.vesselRate.fullDayRate ||
        !this.vesselRate.hourlyRate
      );
    }
  }

  removeSplits() {
    if (
      !this.lineItemIsSplit &&
      this.vesselRate.vesselSplitRates &&
      this.vesselRate.vesselSplitRates.length
    ) {
      const deleteSub = this.vesselSplitRateDAO
        .deleteMultiple(this.vesselId, this.vesselRate.id)
        .subscribe((deleted) => {
          this.lineItemSplits = [];
        });
      this.subs.push(deleteSub);
    }
  }
}
